/**
 * User Preferences Type Definitions
 * Matches the database schema exactly (25 columns)
 * Database table: user_preferences
 */

/**
 * Complete user preferences interface matching all 25 database columns
 * This interface represents the full shape of data stored in the user_preferences table
 */
export interface UserPreferences {
  // Core identification fields
  id: string;
  user_id: string;
  created_at: string;
  updated_at: string;
  
  // Theme preferences
  theme: 'light' | 'dark';
  
  // Email notification master toggle
  email_enabled: boolean;
  
  // Legacy push notifications toggle
  push_notifications: boolean;
  
  // JSONB field for misc preferences
  preferences: Record<string, any>;
  
  // UI/Display preferences
  compact_mode: boolean;
  reduced_motion: boolean;
  dark_mode: boolean;
  theme_color: string;
  
  // Email notification preferences
  email_assessment_reminders: boolean;
  email_progress_updates: boolean;
  email_team_activity: boolean;
  
  // Real-time notification preferences
  browser_notifications: boolean;
  mobile_push_notifications: boolean;
  notification_frequency: 'realtime' | 'hourly' | 'daily' | 'weekly' | null;
  
  // Feature flags
  beta_access: boolean;
  
  // Analytics & tracking
  analytics_enabled: boolean;
  performance_tracking_enabled: boolean;
  
  // Communication preferences
  marketing_emails: boolean;
  product_updates: boolean;
  
  // Privacy & consent
  data_collection_consent: boolean;
}

/**
 * Type for partial updates to user preferences
 * Omits read-only fields (id, user_id, timestamps)
 * Used for PATCH/UPDATE operations where only specific fields need to be changed
 */
export type UserPreferencesUpdate = Partial<Omit<UserPreferences, 'id' | 'user_id' | 'created_at' | 'updated_at'>>;

/**
 * Valid notification frequency options
 * Used for validation before database updates
 */
export const VALID_NOTIFICATION_FREQUENCIES = ['realtime', 'hourly', 'daily', 'weekly'] as const;

/**
 * Default preference values matching database defaults
 * Used when creating new user preference records
 * All values must match the DEFAULT constraints in the database schema
 */
export const DEFAULT_USER_PREFERENCES: Omit<UserPreferences, 'id' | 'user_id' | 'created_at' | 'updated_at'> = {
  // Theme preferences
  theme: 'light',
  
  // Email notification master toggle
  email_enabled: true,
  
  // Legacy push notifications toggle
  push_notifications: false,
  
  // JSONB field
  preferences: {},
  
  // UI/Display preferences
  compact_mode: false,
  reduced_motion: false,
  dark_mode: false,
  theme_color: 'default',
  
  // Email notification preferences
  email_assessment_reminders: true,
  email_progress_updates: true,
  email_team_activity: true,
  
  // Real-time notification preferences
  browser_notifications: true,
  mobile_push_notifications: true,
  notification_frequency: 'realtime',
  
  // Feature flags
  beta_access: false,
  
  // Analytics & tracking
  analytics_enabled: true,
  performance_tracking_enabled: false,
  
  // Communication preferences
  marketing_emails: false,
  product_updates: true,
  
  // Privacy & consent
  data_collection_consent: false,
};

/**
 * Type guard to validate notification frequency values
 * Ensures only valid frequency options are used before database updates
 * 
 * @param value - Value to validate
 * @returns true if value is a valid notification frequency, false otherwise
 * 
 * @example
 * if (isValidNotificationFrequency(userInput)) {
 *   await updatePreferences({ notification_frequency: userInput });
 * }
 */
export function isValidNotificationFrequency(value: unknown): value is 'realtime' | 'hourly' | 'daily' | 'weekly' {
  return typeof value === 'string' && VALID_NOTIFICATION_FREQUENCIES.includes(value as any);
}
